import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check } from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import BillingToggle from "./BillingToggle";
import { navigateToEnterpriseForm } from "@/utils/navigation";

interface PricingPreviewProps {
  planTypes?: string[];
  idPrefix?: string;
}

interface SubPlan {
  credits: number;
  monthlyPrice: number;
  annualPrice: number;
  displayPrice: string;
  uid: string;
  originalName: string;
  urlSlug: string;
}

interface Plan {
  name: string;
  price: string;
  short_description: string;
  features: string[];
  cta: string;
  popular: boolean;
  isEnterprise?: boolean;
  subPlans?: SubPlan[];
  uid?: string;
}

// Fallback data for all plans with updated pricing
const fallbackPlans: Plan[] = [
  {
    name: "Free",
    price: "$0",
    short_description: "Perfect for getting started",
    features: [
      "Single user plan",
      "30 credits per month",
      "No expiry for your credits",
      "Research, plan, ideate and create fast with Studio",
      "Unlock greater insights and multi-level tasks with Reasoning mode",
      "Unlimited* workspaces, projects, brand vaults, and social visual posts"
    ],
    cta: "Start Free",
    popular: false,
    uid: "free-plan"
  },
  {
    name: "Solo",
    price: "$19",
    short_description: "For individual creators and professionals",
    features: [
      "220 credits monthly",
      "Monthly credit plans to suit your needs",
      "Purchase add-on credit packs only when you need to",
      "Generate AI videos with Studio",
      "Add on your social publishing profiles for just $10"
    ],
    cta: "Start Solo",
    popular: true,
    subPlans: [
      {
        credits: 220,
        monthlyPrice: 23,
        annualPrice: 19,
        displayPrice: "$19",
        uid: "solo-220",
        originalName: "SOLO 220",
        urlSlug: "solo-220"
      },
      {
        credits: 520,
        monthlyPrice: 59,
        annualPrice: 49,
        displayPrice: "$49",
        uid: "solo-520",
        originalName: "SOLO 520",
        urlSlug: "solo-520"
      },
      {
        credits: 1020,
        monthlyPrice: 119,
        annualPrice: 99,
        displayPrice: "$99",
        uid: "solo-1020",
        originalName: "SOLO 1020",
        urlSlug: "solo-1020"
      }
    ]
  },
  {
    name: "Team",
    price: "$199",
    short_description: "For teams and growing businesses",
    features: [
      "2020 credits monthly",
      "Team collaboration with 5 user seats",
      "Assign admin or team member permissions",
      "Shared projects, workspaces, and brand vaults across users",
      "Shared publishing profiles"
    ],
    cta: "Start Team",
    popular: false,
    subPlans: [
      {
        credits: 2020,
        monthlyPrice: 239,
        annualPrice: 199,
        displayPrice: "$199",
        uid: "team-2020",
        originalName: "TEAM 2020",
        urlSlug: "team-2020"
      },
      {
        credits: 4020,
        monthlyPrice: 479,
        annualPrice: 399,
        displayPrice: "$399",
        uid: "team-4020",
        originalName: "TEAM 4020",
        urlSlug: "team-4020"
      },
      {
        credits: 10020,
        monthlyPrice: 1199,
        annualPrice: 999,
        displayPrice: "$999",
        uid: "team-10020",
        originalName: "TEAM 10020",
        urlSlug: "team-10020"
      }
    ]
  },
  {
    name: "Enterprise",
    price: "Custom",
    short_description: "Everything in Team, plus:",
    features: [
      "Priority support",
      "Custom login URL",
      "Onboarding with our Customer Success team",
      "Organization-level access control",
      "Optional custom Playbooks, integrations and Agentic frameworks"
    ],
    cta: "Request Quote",
    popular: false,
    isEnterprise: true,
    subPlans: undefined,
    uid: undefined
  }
];

// API service functions
const apiUrl = import.meta.env.VITE_API_URL || "https://api.gimmefy.ai/v2";

interface ApiPlan {
  id: number;
  uid: string;
  total_credits: number;
  display_order: number;
  name: string;
  icon: string;
  signup_image: string;
  signup_headline: string;
  signup_description: string;
  tags: string;
  plan_amount: string;
  url_slug: string;
}

interface PlansResponse {
  success: boolean;
  message: string;
  payload: ApiPlan[];
  meta: Record<string, unknown>;
}

interface PlanGroups {
  [key: string]: ApiPlan[];
}

const plansApi = {
  async getPublicPlans(): Promise<PlansResponse> {
    try {
      const fullUrl = `${apiUrl}/plans/public?filters[plan_type][in]=Free,Solo,Team,Enterprise`;
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching plans:', error);
      throw error;
    }
  }
};

const PricingPreview = ({ planTypes = ['Free', 'Solo', 'Team'], idPrefix = 'pricing' }: PricingPreviewProps) => {
  const navigate = useNavigate();
  const [soloSelection, setSoloSelection] = useState<number>(0);
  const [teamSelection, setTeamSelection] = useState<number>(0);
  const [soloHoverIndex, setSoloHoverIndex] = useState<number | null>(null);
  const [teamHoverIndex, setTeamHoverIndex] = useState<number | null>(null);
  const [plans, setPlans] = useState<Plan[] | PlanGroups>(fallbackPlans);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAnnual, setIsAnnual] = useState<boolean>(true); // Annual by default
  
  // Helper function to get price from credits with annual/monthly support
  const _getPriceFromCredits = (credits: number, planType: string, isAnnual: boolean): number => {
    if (planType === 'Solo') {
      if (credits === 220) return isAnnual ? 19 : 23;
      if (credits === 520) return isAnnual ? 49 : 59;
      if (credits === 1020) return isAnnual ? 99 : 119;
      return isAnnual ? 19 : 23; // default
    } else if (planType === 'Team') {
      if (credits === 2020) return isAnnual ? 199 : 239;
      if (credits === 4020) return isAnnual ? 399 : 479;
      if (credits === 10020) return isAnnual ? 999 : 1199;
      return isAnnual ? 199 : 239; // default
    }
    return 0;
  };

  // Helper function to get default features for each plan type
  const getDefaultFeatures = (planType: string): string[] => {
    switch (planType) {
      case 'Free':
        return ["Single user plan", "30 credits per month", "No expiry for your credits", "Research, plan, ideate and create fast with Studio", "Unlock greater insights and multi-level tasks with Reasoning mode", "Unlimited* workspaces, projects, brand vaults, and social visual posts"];
      case 'Solo':
        return ["Monthly credit plans to suit your needs", "Purchase add-on credit packs only when you need to", "Generate AI videos with Studio", "Add on your social publishing profiles for just $10"];
      case 'Team':
        return ["Team collaboration with 5 user seats", "Assign admin or team member permissions", "Shared projects, workspaces, and brand vaults across users", "Shared publishing profiles"];
      case 'Enterprise':
        return ["Priority support", "Custom login URL", "Onboarding with our Customer Success team", "Organization-level access control", "Optional custom Playbooks, integrations and Agentic frameworks"];
      default:
        return ["Plan features"];
    }
  };

  // Handle plan button click
  const handlePlanClick = (plan: Plan, subPlanIndex?: number) => {
    if (plan.isEnterprise) {
      // Use the utility function to navigate to enterprise form
      navigateToEnterpriseForm(navigate);
      return;
    }

    if (plan.subPlans && subPlanIndex !== undefined) {
      const selectedSubPlan = plan.subPlans[subPlanIndex];
      // Use the url_slug from API for clean navigation
      const url = `/signup/${selectedSubPlan.urlSlug}`;
      window.location.href = url;
    } else {
      // For Free plan, use the url_slug from API
      if (plan.uid) {
        window.location.href = '/signup/free';
      } else {
        // Fallback for other plans
        const url = `/signup/${plan.name.replace(/\s+/g, '-').toLowerCase()}`;
        window.location.href = url;
      }
    }
  };
  
  // Fetch plans from API on component mount
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const response = await plansApi.getPublicPlans();
        
        // Transform API data to match our Plan interface with new pricing structure
        if (response.payload && response.payload.length > 0) {
          // Group plans by type (Free, Solo, Team)
          const planGroups = response.payload.reduce((groups: PlanGroups, apiPlan) => {
            let planType = 'Free';
            if (apiPlan.name.toUpperCase().startsWith('SOLO')) planType = 'Solo';
            else if (apiPlan.name.toUpperCase().startsWith('TEAM')) planType = 'Team';
            
            if (!groups[planType]) {
              groups[planType] = [];
            }
            groups[planType].push(apiPlan);
            return groups;
          }, {});

          // Store the raw API data for filtering
          setPlans(planGroups);
        } else {
          console.log('API returned empty payload, using fallback data');
          setPlans(fallbackPlans);
        }
      } catch (err) {
        console.error('Failed to fetch plans:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch plans');
        setPlans(fallbackPlans);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
     
  }, []);

  // Transform plans based on annual/monthly selection without API call
  const transformedPlans = useMemo(() => {
    if (typeof plans === 'object' && !Array.isArray(plans)) {
      // Transform grouped plans to match our Plan interface
      const transformed = Object.entries(plans).map(([planType, planList]) => {
        const firstPlan = (planList as { total_credits: number; uid: string; signup_description: string; tags: string }[])[0];
        
        // For Solo and Team, create sub-plans with actual API pricing
        let subPlans = undefined;
        if (planType === 'Solo' || planType === 'Team') {
          // Filter plans by annual/monthly based on current selection
          const filteredPlans = (planList as { total_credits: number; uid: string; name: string; tags: string; plan_amount: string; url_slug: string }[]).filter(plan => {
            if (isAnnual) {
              return plan.tags === 'yearly';
            } else {
              return plan.tags === 'monthly';
            }
          });
          
          subPlans = filteredPlans.map((plan) => ({
            credits: plan.total_credits,
            monthlyPrice: plan.tags === 'yearly' ? parseFloat(plan.plan_amount) / 12 : parseFloat(plan.plan_amount),
            annualPrice: plan.tags === 'yearly' ? parseFloat(plan.plan_amount) : parseFloat(plan.plan_amount) * 12,
            displayPrice: plan.tags === 'yearly' ? `$${(parseFloat(plan.plan_amount) / 12).toFixed(0)}` : `$${parseFloat(plan.plan_amount)}`,
            uid: plan.uid,
            originalName: plan.name,
            urlSlug: plan.url_slug
          }));
        }

        return {
          name: planType,
          price: planType === 'Free' ? '$0' : 
                 planType === 'Solo' && subPlans && subPlans.length > 0 ? 
                   (isAnnual ? `$${(subPlans[0].annualPrice / 12).toFixed(0)}` : `$${subPlans[0].monthlyPrice}`) :
                 planType === 'Team' && subPlans && subPlans.length > 0 ? 
                   (isAnnual ? `$${(subPlans[0].annualPrice / 12).toFixed(0)}` : `$${subPlans[0].monthlyPrice}`) : 'Custom',
          short_description: (planType === 'Solo' || planType === 'Team') ? 
            (firstPlan as { short_description?: string; signup_description: string }).short_description || firstPlan.signup_description || `${planType} plan` :
            firstPlan.signup_description || `${planType} plan`,
          features: getDefaultFeatures(planType),
          cta: planType === 'Enterprise' ? 'Request Quote' : `Start ${planType}`,
          popular: planType === 'Solo',
          isEnterprise: planType === 'Enterprise',
          subPlans: subPlans,
          uid: planType === 'Free' ? firstPlan.uid : undefined
        };
      });

      // Add Enterprise plan if not in API response
      const hasEnterprise = transformed.some(plan => plan.name === 'Enterprise');
      if (!hasEnterprise) {
        const enterprisePlan = {
          name: "Enterprise",
          price: "Custom",
          short_description: "Everything in Team, plus:",
          features: [
            "Priority support",
            "Custom login URL",
            "Onboarding with our Customer Success team",
            "Organization-level access control",
            "Optional custom Playbooks, integrations and Agentic frameworks"
          ],
          cta: "Request Quote",
          popular: false,
          isEnterprise: true,
          subPlans: undefined,
          uid: undefined
        }; 
        transformed.push(enterprisePlan);
      }

      return transformed;
    }
    return plans;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans, isAnnual, soloSelection, teamSelection]);
  
  const filteredPlans = transformedPlans.filter(plan => planTypes.includes(plan.name));

  const _getPlanPrice = (plan: Plan, _index: number) => {
    if (plan.name === "Solo" && plan.subPlans) {
      const hoverIndex = soloHoverIndex !== null ? soloHoverIndex : soloSelection;
      const subPlan = plan.subPlans[hoverIndex];
      if (isAnnual) {
        // For yearly plans, show monthly equivalent
        return `$${(subPlan.annualPrice / 12).toFixed(0)}`;
      } else {
        // For monthly plans, show actual monthly price
        return `$${subPlan.monthlyPrice}`;
      }
    }
    if (plan.name === "Team" && plan.subPlans) {
      const hoverIndex = teamHoverIndex !== null ? teamHoverIndex : teamSelection;
      const subPlan = plan.subPlans[hoverIndex];
      if (isAnnual) {
        // For yearly plans, show monthly equivalent
        return `$${(subPlan.annualPrice / 12).toFixed(0)}`;
      } else {
        // For monthly plans, show actual monthly price
        return `$${subPlan.monthlyPrice}`;
      }
    }
    return plan.price;
  };

  const getMainPlanPrice = (plan: Plan) => {
    if (plan.name === "Solo" && plan.subPlans && plan.subPlans.length > 0) {
      // Use the currently selected subplan instead of always the first one
      const selectedSubPlan = plan.subPlans[soloSelection];
      if (isAnnual) {
        // For yearly plans, show monthly equivalent
        return `$${(selectedSubPlan.annualPrice / 12).toFixed(0)}`;
      } else {
        // For monthly plans, show actual monthly price
        return `$${selectedSubPlan.monthlyPrice}`;
      }
    }
    if (plan.name === "Team" && plan.subPlans && plan.subPlans.length > 0) {
      // Use the currently selected subplan instead of always the first one
      const selectedSubPlan = plan.subPlans[teamSelection];
      if (isAnnual) {
        // For yearly plans, show monthly equivalent
        return `$${(selectedSubPlan.annualPrice / 12).toFixed(0)}`;
      } else {
        // For monthly plans, show actual monthly price
        return `$${selectedSubPlan.monthlyPrice}`;
      }
    }
    return plan.price;
  };

  const getPlanCredits = (plan: Plan) => {
    if (plan.name === "Solo" && plan.subPlans) {
      const hoverIndex = soloHoverIndex !== null ? soloHoverIndex : soloSelection;
      return plan.subPlans[hoverIndex].credits;
    }
    if (plan.name === "Team" && plan.subPlans) {
      const hoverIndex = teamHoverIndex !== null ? teamHoverIndex : teamSelection;
      return plan.subPlans[hoverIndex].credits;
    }
    return null;
  };

  const getUpdatedFeatures = (plan: Plan) => {
    const credits = getPlanCredits(plan);
    if (credits && plan.subPlans) {
      const updatedFeatures = [...plan.features];
      updatedFeatures[0] = `${credits.toLocaleString()} credits monthly`;
      return updatedFeatures;
    }
    return plan.features;
  };

  const getBillingText = (plan: Plan) => {
    if ((plan.name === "Solo" || plan.name === "Team") && isAnnual && plan.subPlans && plan.subPlans.length > 0) {
      // Use the currently selected subplan instead of always the first one
      let selectedSubPlan;
      if (plan.name === "Solo") {
        selectedSubPlan = plan.subPlans[soloSelection];
      } else if (plan.name === "Team") {
        selectedSubPlan = plan.subPlans[teamSelection];
      }
      
      if (selectedSubPlan) {
        const _monthlyEquivalent = (selectedSubPlan.annualPrice / 12).toFixed(0);
        const _annualTotal = selectedSubPlan.annualPrice;
        return `/mo, paid annually`;
      }
    }
    if (plan.price !== "Custom" && plan.name !== "Free") {
      return "/mo";
    }
    return "";
  };



  return (
    <>
      <section className="py-24 bg-background">
        <div className="container mx-auto px-6">
          <div className="text-center mb-5">
            <h2 id="pricing-main-heading" className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              Choose your plan
            </h2>
          </div>
          
          {/* Billing Toggle - Only show if Solo or Team plans are included */}
          {(planTypes.includes('Solo') || planTypes.includes('Team')) && !loading && (
            <BillingToggle 
              id={`${idPrefix}-billing-toggle`}
              isAnnual={isAnnual} 
              onToggle={setIsAnnual} 
            />
          )}
          
          {/* Loading and Error States */}
          {loading && (
            <div className="text-center py-12">
              <div className="text-lg text-muted-foreground">Loading plans...</div>
            </div>
          )}
          
          {error && (
            <div className="text-center py-12">
              <div className="text-lg text-red-600 mb-4">Error loading plans: {error}</div>
              <div className="text-sm text-muted-foreground">Using fallback data</div>
            </div>
          )}
          
          {/* Plans Grid - Only show when not loading */}
          {!loading && (
            <>
          <div className="max-w-7xl mx-auto mb-12">
            {/* Most Popular Badge - Aligned to Solo card */}
            <div className={`grid gap-8 ${filteredPlans.length === 2 ? 'md:grid-cols-2' : filteredPlans.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2 lg:grid-cols-4'} mb-4`}>
              {filteredPlans.map((plan, index) => (
                <div key={`badge-${index}`} className="flex justify-center">
                  {plan.popular && (
                    <span className="bg-primary/10 border border-primary/20 text-primary px-3 py-1 rounded-full text-sm font-medium inline-block">
                      Most Popular
                    </span>
                  )}
                </div>
              ))}
            </div>
            
            {/* Cards Grid */}
            <div className={`grid gap-8 ${filteredPlans.length === 2 ? 'md:grid-cols-2' : filteredPlans.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2 lg:grid-cols-4'}`}>
              {filteredPlans.map((plan, index) => (
                <div key={index} className="flex">
                  <Card 
                    className={`p-8 border shadow-elegant transition-all duration-300 hover:-translate-y-2 flex flex-col w-full h-full ${plan.popular ? 'bg-card ring-2 ring-primary/30 scale-105' : 'bg-card hover:shadow-primary'}`}
                  >
                    <div className="text-center mb-8">
                      <h3 className="text-2xl font-bold mb-4 text-foreground">
                        {plan.name}
                      </h3>
                      
                      <div className="mb-2">
                        <span className="text-4xl font-bold text-foreground">
                          {getMainPlanPrice(plan)}
                        </span>
                        <span className="text-sm text-muted-foreground">{getBillingText(plan)}</span>
                      </div>
                      
                      {plan.subPlans && (
                        <div className="mb-4">
                          <Select 
                            value={plan.name === "Solo" ? soloSelection.toString() : teamSelection.toString()}
                            onValueChange={(value) => {
                              if (plan.name === "Solo") {
                                setSoloSelection(parseInt(value));
                              } else if (plan.name === "Team") {
                                setTeamSelection(parseInt(value));
                              }
                            }}
                          >
                            <SelectTrigger className="w-full bg-card border">
                              <div className="flex-1 text-left">
                                {plan.subPlans[plan.name === "Solo" ? soloSelection : teamSelection].credits.toLocaleString()} credits / month
                              </div>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-card border min-w-[200px]">
                              {plan.subPlans.map((subPlan, subIndex) => (
                                <SelectItem 
                                  key={subIndex} 
                                  value={subIndex.toString()} 
                                  onMouseEnter={() => {
                                    if (plan.name === "Solo") {
                                      setSoloHoverIndex(subIndex);
                                    } else if (plan.name === "Team") {
                                      setTeamHoverIndex(subIndex);
                                    }
                                  }}
                                  onMouseLeave={() => {
                                    if (plan.name === "Solo") {
                                      setSoloHoverIndex(null);
                                    } else if (plan.name === "Team") {
                                      setTeamHoverIndex(null);
                                    }
                                  }}
                                >
                                  {subPlan.credits.toLocaleString()} credits / month
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      
                      <p className="text-muted-foreground">
                        {plan.short_description}
                      </p>
                    </div>
                    
                    <ul className="space-y-3 mb-8 flex-grow">
                      {getUpdatedFeatures(plan).map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center space-x-3">
                          <Check className="h-4 w-4 flex-shrink-0 text-primary" />
                          <span className="text-sm text-foreground">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>
                    
                    <div className="mt-auto">
                      <Button 
                        id={`${idPrefix}-cta-${plan.name.toLowerCase()}`}
                        variant="default" 
                        className="w-full"
                        onClick={() => {
                          if (plan.name === "Solo") {
                            handlePlanClick(plan, soloSelection);
                          } else if (plan.name === "Team") {
                            handlePlanClick(plan, teamSelection);
                          } else {
                            handlePlanClick(plan);
                          }
                        }}
                      >
                        {plan.cta}
                      </Button>
                      {plan.name === "Free" && (
                        <p className="text-xs text-muted-foreground text-center mt-2">
                          No credit card required
                        </p>
                      )}
                    </div>
                  </Card>
                </div>
              ))}
            </div>
          </div>
          
          <div className="text-center mt-16">
            
          </div>
        </>
      )}
        </div>
      </section>
    </>
  );
};

export default PricingPreview;
